import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx, sortCx } from '../../../utils/cx';

const styles = sortCx({
  base: [
    'relative w-full text-secondary outline-focus-ring transition duration-150 ease-linear',
    'focus-visible:outline-2 focus-visible:outline-offset-2'
  ].join(' '),
  tone: {
    surface: 'bg-primary text-secondary',
    subtle: 'bg-secondary_alt text-secondary',
    muted: 'bg-primary_alt text-secondary',
    brand: 'bg-brand-solid text-white',
    transparent: 'bg-transparent'
  },
  padding: {
    none: 'p-0',
    xs: 'p-2',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8'
  },
  radius: {
    none: 'rounded-none',
    sm: 'rounded-lg',
    md: 'rounded-xl',
    lg: 'rounded-2xl',
    xl: 'rounded-[1.5rem]',
    pill: 'rounded-full'
  },
  border: {
    none: 'ring-0 ring-transparent',
    subtle: 'ring-1 ring-inset ring-secondary',
    strong: 'ring-1 ring-inset ring-primary'
  },
  shadow: {
    none: 'shadow-none',
    xs: 'shadow-xs',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg'
  }
});

export type BoxTone = keyof typeof styles.tone;
export type BoxPadding = keyof typeof styles.padding;
export type BoxRadius = keyof typeof styles.radius;
export type BoxBorder = keyof typeof styles.border;
export type BoxShadow = keyof typeof styles.shadow;

export interface BoxProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  /** Background and text treatment */
  tone?: BoxTone;
  /** Padding applied to the container */
  padding?: BoxPadding;
  /** Corner rounding */
  radius?: BoxRadius;
  /** Outline ring / border intensity */
  border?: BoxBorder;
  /** Drop shadow depth */
  shadow?: BoxShadow;
  /** Optional section heading rendered above the body */
  title?: ReactNode;
  /** Optional complementary content below the heading */
  helperText?: ReactNode;
}

export const Box = forwardRef<HTMLDivElement, BoxProps>(function Box(
  {
    tone = 'surface',
    padding = 'md',
    radius = 'md',
    border = 'subtle',
    shadow = 'none',
    className,
    title,
    helperText,
    children,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      data-tone={tone}
      data-padding={padding}
      data-radius={radius}
      data-border={border}
      data-shadow={shadow}
      className={cx(
        styles.base,
        styles.tone[tone],
        styles.padding[padding],
        styles.radius[radius],
        styles.border[border],
        styles.shadow[shadow],
        className
      )}
      {...props}
    >
      {(title || helperText) && (
        <header className="mb-3 flex flex-col gap-1 text-left">
          {title && (
            <p className="text-md font-semibold text-current" data-box-title>
              {title}
            </p>
          )}
          {helperText && (
            <p
              className={cx(
                'text-sm text-tertiary',
                tone === 'brand' && 'text-white/80'
              )}
              data-box-helper
            >
              {helperText}
            </p>
          )}
        </header>
      )}
      {children}
    </div>
  );
});

Box.displayName = 'UntitledUI.Box';
